import React, {
  useEffect,
  useMemo,
  useRef,
  createContext,
  useCallback,
  useContext
} from 'react'
import PropTypes from 'prop-types'
import _ from 'lodash'

const isMac = window.navigator.platform.toLowerCase().includes('mac')

/**
 * @param {string | string[]} keys the key to press for the shortcut, if an array is passed only the first is shown to the user, but the latter options can also work if the OS (ie. windows) sends them
 * @returns {(KeyboardEvent) => bool}
 */
export function getMatcher(
  { name, group },
  keys,
  { ctrlOrCommand = false, shift = false, altOrOpt = false } = {}
) {
  keys = (Array.isArray(keys) ? keys : [keys]).map(char => char.toLowerCase())

  /**
   * @param {KeyboardEvent} event
   */
  const match = event => {
    const ctrlOrCommandState = isMac ? event.metaKey : event.ctrlKey

    return (
      event.altKey === altOrOpt &&
      event.shiftKey === shift &&
      ctrlOrCommand === ctrlOrCommandState &&
      keys.includes(event.key.toLowerCase())
    )
  }

  const key = keys[0]

  return {
    match,
    name,
    group,
    combination: `${ctrlOrCommand ? (isMac ? 'cmd+' : 'ctrl+') : ''}${
      shift ? 'shift+' : ''
    }${altOrOpt ? (isMac ? 'opt+' : 'alt+') : ''}${key}`
  }
}

const ShortcutsContext = createContext({})
export function ShortcutsProvider({ children, ...props }) {
  const registeredRef = useRef([])
  const listenersRef = useRef([])

  const onKeyDown = useCallback(event => {
    for (const [matcher, callback] of listenersRef.current) {
      if (matcher.match(event)) {
        event.preventDefault()
        event.stopPropagation()

        callback(event)
      }
    }
  }, [])

  useEffect(() => {
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [onKeyDown])

  const register = useCallback(matcher => {
    registeredRef.current.push(matcher)

    return () => {
      const index = registeredRef.current.indexOf(matcher)
      if (index >= 0) {
        registeredRef.current.splice(index, 1)
      }
    }
  }, [])

  const listen = useCallback(
    (matcher, callback) => {
      // If Matcher is already in the listenersRef just return an empty function
      if (
        listenersRef.current.find(listener => listener[0].name === matcher.name)
      ) {
        return () => {}
      }

      const unregister = register(matcher)
      const pair = [
        matcher,
        _.debounce(callback, 500, { leading: true, trailing: false })
      ]

      listenersRef.current.push(pair)

      return () => {
        unregister()

        const index = listenersRef.current.indexOf(pair)
        if (index >= 0) {
          listenersRef.current.splice(index, 1)
        }
      }
    },
    [register]
  )

  const getRegistered = useCallback(() => {
    return registeredRef.current
  }, [])

  return (
    <ShortcutsContext.Provider
      {...props}
      value={{
        register,
        listen,
        getRegistered
      }}
    >
      {children}
    </ShortcutsContext.Provider>
  )
}

ShortcutsProvider.propTypes = {
  children: PropTypes.node.isRequired
}

export function useRegisteredShortcuts() {
  const { getRegistered } = useContext(ShortcutsContext)

  return getRegistered
}

export function useGlobalShortcut(matcher, callback) {
  const { listen } = useContext(ShortcutsContext)
  const matcherRef = useRef(matcher)

  useEffect(() => {
    const unlisten = listen(matcherRef.current, callback)

    return () => {
      unlisten()
    }
  }, [callback, listen])
}

export function useLocalShortcut(
  matcher,
  callback,
  { existingOnKeyDown = null } = {}
) {
  const { register } = useContext(ShortcutsContext)
  const matcherRef = useRef(matcher)

  useEffect(() => {
    const unregister = register(matcherRef.current)

    return () => {
      unregister()
    }
  }, [callback, register])

  return useMemo(() => {
    return {
      onKeyDown: event => {
        existingOnKeyDown?.(event)

        if (matcherRef.current.match(event)) {
          event.preventDefault()
          event.stopPropagation()

          callback(event)
        }
      }
    }
  }, [callback, existingOnKeyDown])
}
